
import { Component, Vue } from 'vue-property-decorator';
import Chart from 'chart.js/auto';
import { ChurnDashboardModel } from '@/models/churn_dashboard';
import { TierDistributionItem } from '@/types/churn';

const TIER_ORDER = ['critical', 'high', 'medium', 'low'];

@Component({})
export default class ChurnDashboard extends Vue {
    public model: ChurnDashboardModel = new ChurnDashboardModel();
    public isLoading: boolean = false;
    public error: string = '';
    private chart: any = null;

    public packageHeaders = [
        { text: 'Package', value: 'name', sortable: true },
        { text: 'Total', value: 'total', sortable: true },
        { text: 'Critical', value: 'critical', sortable: false, align: 'center' },
        { text: 'High', value: 'high', sortable: false, align: 'center' },
        { text: 'Medium', value: 'medium', sortable: false, align: 'center' },
        { text: 'Low', value: 'low', sortable: false, align: 'center' },
    ];

    public get orderedTiers(): TierDistributionItem[] {
        return [...this.model.distribution.tiers].sort(
            (a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier),
        );
    }

    public async created(): Promise<void> {
        await this.refreshData();
    }

    public async refreshData(): Promise<void> {
        this.isLoading = true;
        this.error = '';
        try {
            await this.model.fetch();
            this.$nextTick(() => this.renderChart());
        } catch (err: any) {
            this.error = err.message || 'Failed to load dashboard data';
        } finally {
            this.isLoading = false;
        }
    }

    public navigateToMerchants(tier?: string): void {
        const query: any = {};
        if (tier) { query.tier = tier; }
        this.$router.push({ name: 'churn_merchants', query });
    }

    public navigateToChurnedPredicted(): void {
        this.$router.push({
            name: 'churn_churned_predicted',
            query: { date: this.model.yesterdayUninstalls.date },
        });
    }

    public navigateToMerchantsByPackage(tier: string, packageId: number): void {
        this.$router.push({
            name: 'churn_merchants',
            query: { tier, billing_package_id: packageId.toString() },
        });
    }

    public formatDate(dateStr: string): string {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    }

    public getTierIcon(tier: string): string {
        const icons: Record<string, string> = {
            critical: 'mdi-alert-octagon',
            high: 'mdi-alert-circle',
            medium: 'mdi-alert',
            low: 'mdi-check-circle',
        };
        return icons[tier] || 'mdi-help-circle';
    }

    public beforeDestroy(): void {
        if (this.chart) {
            this.chart.destroy();
        }
    }

    private renderChart(): void {
        const ctx = document.getElementById('riskDistributionChart');
        if (!(ctx instanceof HTMLCanvasElement)) { return; }
        if (this.chart) { this.chart.destroy(); }

        const tiers = this.orderedTiers;
        if (tiers.length === 0) { return; }

        this.chart = new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels: tiers.map((t) => t.label),
                datasets: [{
                    data: tiers.map((t) => t.count),
                    backgroundColor: tiers.map((t) => t.color),
                    borderWidth: 3,
                    borderColor: '#fff',
                    hoverBorderWidth: 4,
                    hoverOffset: 8,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: true,
                cutout: '60%',
                plugins: {
                    legend: {
                        position: 'right',
                        labels: {
                            padding: 16,
                            usePointStyle: true,
                            pointStyle: 'circle',
                            font: { size: 13, weight: '500' },
                            generateLabels: (chart: any) => {
                                const datasets = chart.data.datasets;
                                return datasets[0].data.map((data: any, i: number) => ({
                                    text: `${chart.data.labels[i]}: ${data.toLocaleString()}`,
                                    fillStyle: datasets[0].backgroundColor[i],
                                    strokeStyle: datasets[0].backgroundColor[i],
                                    index: i,
                                }));
                            },
                        },
                    },
                    tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        padding: 12,
                        titleFont: { size: 14 },
                        bodyFont: { size: 13 },
                        callbacks: {
                            label: (context: any) => {
                                const tier = tiers[context.dataIndex];
                                return ` ${tier.count.toLocaleString()} merchants (${tier.percentage}%)`;
                            },
                        },
                    },
                },
                onClick: (_event: any, elements: any[]) => {
                    if (elements.length > 0) {
                        const tierName = tiers[elements[0].index].tier;
                        this.navigateToMerchants(tierName);
                    }
                },
            },
        });
    }
}
